// "Quién va al evento" (plan 4.5). Dos bloques:
// a) Responsable del montaje (1 persona): equipo propio o un extra, con horas
//    por tramos y coste de cada opción.
// b) Personal del menú según los ratios de cada menú (camareros y extras),
//    siempre externo a la tarifa del extra.
// Réplica exacta de las funciones del backend (reglas_comerciales_routes.py:
// resolver_tramos, resolver_personal_menu, calcular_quien_va, horario_jornada)
// para que la ficha recalcule al instante mientras se edita. Los valores por
// defecto y las tarifas vienen siempre del backend
// (GET /presupuestos/{id}/quien-va-evento).

export const TRAMOS_QUIEN_VA = [
  { clave: 'carga', etiqueta: 'Llegada a la nave y carga' },
  { clave: 'ida', etiqueta: 'Ida' },
  { clave: 'montaje', etiqueta: 'Montaje' },
  { clave: 'vuelta_montaje', etiqueta: 'Vuelta a la nave tras montar', segundoViaje: true },
  { clave: 'evento', etiqueta: 'Evento' },
  { clave: 'ida_recogida', etiqueta: 'Ida a recoger', segundoViaje: true },
  { clave: 'desmontaje', etiqueta: 'Desmontaje y recogida' },
  { clave: 'vuelta', etiqueta: 'Vuelta' },
  { clave: 'descarga', etiqueta: 'Descarga' },
];

// Tramos que solo existen si se va y vuelve a recoger (dos viajes)
export const TRAMOS_SEGUNDO_VIAJE = ['vuelta_montaje', 'ida_recogida'];
// Tramos de la recogida (segundo viaje) y orden de cada bloque de la línea de tiempo
export const TRAMOS_RECOGIDA = ['ida_recogida', 'desmontaje', 'vuelta', 'descarga'];
const PRIMER_VIAJE = ['carga', 'ida', 'montaje', 'evento', 'vuelta_montaje'];
const SE_QUEDA = ['carga', 'ida', 'montaje', 'evento', 'desmontaje', 'vuelta', 'descarga'];

// Quién hace la recogida (réplica de QUIEN_RECOGE del backend)
export const QUIEN_RECOGE = [
  { clave: 'responsable', etiqueta: 'El mismo responsable' },
  { clave: 'propio', etiqueta: 'Equipo propio' },
  { clave: 'extra', etiqueta: 'Un extra' },
  { clave: 'otro_evento', etiqueta: 'Aprovechando otro evento' },
];

export const CAMPOS_PERSONAL_MENU = ['camareros', 'extras', 'horas'];

const CLAVES = TRAMOS_QUIEN_VA.map(t => t.clave);

const entero = (v) => {
  const n = parseInt(v, 10);
  return Number.isNaN(n) ? 0 : n;
};

const numero = (v) => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
};

const redondear2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

const tieneValor = (v) => v !== undefined && v !== null && v !== '';

// Tramos efectivos: los editados a mano con su valor; el resto, por defecto.
// La vuelta, si no se ha editado, es igual que la ida. Lo mismo la vuelta tras
// montar y la ida a recoger cuando hay dos viajes (por defecto > 0).
export const resolverTramos = (defecto = {}, valores = {}, editados = []) => {
  const fijados = (editados || []).filter(t => CLAVES.includes(t) && valores && tieneValor(valores[t]));
  const res = {};
  CLAVES.forEach((t) => { res[t] = entero(defecto?.[t]); });
  fijados.forEach((t) => { res[t] = Math.max(0, entero(valores[t])); });
  if (!fijados.includes('vuelta')) res.vuelta = res.ida;
  TRAMOS_SEGUNDO_VIAJE.forEach((t) => {
    if (!fijados.includes(t) && entero(defecto?.[t]) > 0) res[t] = res.ida;
  });
  return res;
};

// Viajes de ida y vuelta de la furgoneta: 2 si se va y vuelve a recoger
export const viajesDeTramos = (tramos = {}) => (TRAMOS_SEGUNDO_VIAJE.some(t => entero(tramos?.[t]) > 0) ? 2 : 1);

// Minutos que tiene que estar quien va para cubrir las filas marcadas
// "Lo cubre la persona que va al evento" (la más larga)
// (con tandas, la tanda más larga)
export const minutosServicioCubierto = (filas = []) => (filas || []).reduce((max, f) => {
  if (!f?.cubre_responsable || entero(f.camareros) + entero(f.extras) <= 0) return max;
  const tandas = Array.isArray(f.tandas) ? f.tandas : [];
  if (tandas.length > 0) return Math.max(max, ...tandas.map(minutosTanda));
  return Math.max(max, Math.ceil(Math.round(numero(f.horas) * 60 * 10000) / 10000));
}, 0);

// Tramos por defecto según se quede (evento = duración, ampliado si el servicio
// que cubre es más largo) o se vaya y vuelva a recoger (evento 0 y dos viajes
// más con los minutos de la ida; otro día, el primer día termina al acabar el
// montaje o el servicio que cubre). Con hora por servicio, si se va y vuelve,
// antes hace los montajes intermedios desde dentro (evento = minutosDentro
// como mínimo). Réplica de aplicar_permanencia del backend.
export const aplicarPermanencia = (base = {}, dosViajes = false, minutosCubiertos = 0, otroDia = false,
  minutosDentro = 0) => {
  const t = {};
  CLAVES.forEach((k) => { t[k] = entero(base?.[k]); });
  if (dosViajes) {
    t.evento = Math.max(otroDia ? Math.max(0, entero(minutosCubiertos)) : 0, Math.max(0, entero(minutosDentro)));
    t.vuelta_montaje = t.ida;
    t.ida_recogida = t.ida;
  } else {
    t.vuelta_montaje = 0;
    t.ida_recogida = 0;
    if (minutosCubiertos > t.evento) t.evento = minutosCubiertos;
  }
  return t;
};

// Por defecto, una fila de exactamente 1 camarero y 0 extras la cubre la
// persona que va al evento (el responsable del montaje), pero solo si es la
// única persona del personal del menú del evento (o de la jornada).
export const cubreResponsableDefecto = (camareros, extras, otrasPersonas = 0) => (
  entero(camareros) === 1 && entero(extras) === 0 && entero(otrasPersonas) === 0
);

// Personas externas de una fila (0 si la cubre la persona que va al evento)
export const personasFila = (f) => (f?.cubre_responsable ? 0 : entero(f?.camareros) + entero(f?.extras));

// ===== Tandas de camareros (20-oct-2026): unos a una hora y otros a otra =====
// Réplica de reglas_comerciales_routes (minutos_tanda, horas_de_tandas,
// tandas_resueltas, horas_persona_fila). En personal_menu_editado[clave].tandas:
// [{camareros, hora_inicio, hora_fin}] (1 a MAX_TANDAS). Con tandas, los
// camareros del servicio son la suma; cada camarero cuenta las horas de su
// tanda; `horas` de la fila (las de los extras y los textos) es la media
// ponderada por camareros redondeada a la media hora (mínimo 0,5).
export const MAX_TANDAS = 6;

// "9:5" -> "09:05", "09:30:00" -> "09:30"; '' si no es una hora posible
export const normalizarHoraEscrita = (valor) => {
  const m = String(valor ?? '').trim().match(/^(\d{1,2})\s*[:.hH]\s*(\d{1,2})(?::\d{1,2})?$/);
  if (!m) return '';
  const h = parseInt(m[1], 10);
  const mi = parseInt(m[2], 10);
  if (h > 23 || mi > 59) return '';
  return `${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')}`;
};

// Minutos de una tanda (pasando la medianoche si hace falta); 0 sin horas
export const minutosTanda = (t) => {
  const a = minutosDeHora(t?.hora_inicio);
  const b = minutosDeHora(t?.hora_fin);
  if (a === null || b === null) return 0;
  return (((b - a) % 1440) + 1440) % 1440;
};

export const horasDeTandas = (tandas = []) => {
  const lista = tandas || [];
  const totalCam = lista.reduce((s, t) => s + Math.max(0, entero(t?.camareros)), 0);
  if (totalCam <= 0) return 0;
  const mediaMin = lista.reduce((s, t) => s + Math.max(0, entero(t?.camareros)) * minutosTanda(t), 0) / totalCam;
  const medias = Math.floor(mediaMin / 30 + 0.5);
  return Math.max(0.5, medias / 2);
};

// Tandas limpias (para comparar y guardar): {camareros, hora_fin, hora_inicio}
// en este orden, sin las que no llevan camareros
export const limpiarTandas = (tandas) => (Array.isArray(tandas) ? tandas : [])
  .filter(t => t && entero(t.camareros) > 0)
  .map(t => ({
    camareros: entero(t.camareros),
    hora_fin: normalizarHoraEscrita(t.hora_fin) || String(t.hora_fin || ''),
    hora_inicio: normalizarHoraEscrita(t.hora_inicio) || String(t.hora_inicio || ''),
  }));

// Tandas con sus horas, para pintar y calcular
export const tandasResueltas = (tandas) => limpiarTandas(tandas).map(t => ({
  camareros: t.camareros, hora_inicio: t.hora_inicio, hora_fin: t.hora_fin, horas: redondear2(minutosTanda(t) / 60),
}));

// Horas-persona de una fila: con tandas, cada camarero con las horas de su
// tanda y los extras con las horas de la fila (0 si la cubre quien va)
export const horasPersonaFila = (f) => {
  if (f?.cubre_responsable) return 0;
  const tandas = Array.isArray(f?.tandas) ? f.tandas : [];
  if (tandas.length > 0) {
    return tandas.reduce((s, t) => s + entero(t.camareros) * minutosTanda(t) / 60, 0)
      + entero(f.extras) * numero(f.horas);
  }
  return personasFila(f) * numero(f?.horas);
};

// Fila de solape de montaje (hora por servicio): solo aviso hasta que se
// decide. Sin decidir (`solape_pendiente`) va con 0 extras y no admite
// ediciones; con "Añadir 1 extra", 1 persona más mientras se sirve el
// servicio anterior (editable). No la cubre el responsable y no cuenta para
// decidir si él cubre las demás. Ignorada, no hay fila
export const ORIGEN_SOLAPE = 'solape_montaje';
const esSolape = (f) => f?.origen === ORIGEN_SOLAPE;

// Personal del menú: valores por defecto de cada fila (`defecto`) con lo
// editado a mano por clave ({ clave: { camareros, extras, horas, cubre_responsable } }).
export const resolverPersonalMenu = (filas = [], editado = {}) => {
  const previas = (filas || []).map((f) => {
    const cambios = (f?.solape_pendiente ? null : (editado && editado[f.clave])) || {};
    const valores = { ...(f.defecto || {}) };
    const editados = [];
    CAMPOS_PERSONAL_MENU.forEach((campo) => {
      if (tieneValor(cambios[campo])) {
        valores[campo] = campo === 'horas' ? redondear2(Math.max(0, numero(cambios[campo]))) : Math.max(0, entero(cambios[campo]));
        editados.push(campo);
      }
    });
    if (typeof cambios.cubre_responsable === 'boolean') {
      valores.cubre_responsable = cambios.cubre_responsable;
      editados.push('cubre_responsable');
    }
    // Tandas de camareros (no en las filas de solape): suma y media, siempre sincronizadas
    const tandas = esSolape(f) ? [] : tandasResueltas(cambios.tandas);
    if (tandas.length > 0) {
      valores.tandas = tandas;
      valores.camareros = tandas.reduce((s, t) => s + t.camareros, 0);
      valores.horas = horasDeTandas(tandas);
      ['camareros', 'horas', 'tandas'].forEach((c) => { if (!editados.includes(c)) editados.push(c); });
    } else {
      delete valores.tandas;
    }
    return { f, valores, editados };
  });
  const total = previas.reduce((s, p) => (esSolape(p.f) ? s : s + entero(p.valores.camareros) + entero(p.valores.extras)), 0);
  return previas.map(({ f, valores, editados }) => {
    if (esSolape(f)) {
      if (!editados.includes('cubre_responsable')) valores.cubre_responsable = false;
    } else if (!editados.includes('cubre_responsable')) {
      const propias = entero(valores.camareros) + entero(valores.extras);
      valores.cubre_responsable = cubreResponsableDefecto(valores.camareros, valores.extras, total - propias);
    }
    return { ...f, ...valores, editados };
  });
};

export const totalPersonalMenu = (filas = [], tarifaExtra = 0) => {
  let personas = 0;
  let horasPersona = 0;
  (filas || []).forEach((f) => {
    personas += personasFila(f);
    // Con tandas, cada camarero con las horas de su tanda
    horasPersona += horasPersonaFila(f);
  });
  return { personas, horasPersona: redondear2(horasPersona), coste: redondear2(horasPersona * (Number(tarifaExtra) || 0)) };
};

// Recogida guardada, normalizada (réplica de recogida_de del backend). null si se queda.
export const recogidaDe = (guardado) => {
  const g = guardado || {};
  if (g.permanencia !== 'se_va') return null;
  const r = (g.recogida && typeof g.recogida === 'object') ? g.recogida : {};
  const tipo = r.tipo === 'otro_dia' ? 'otro_dia' : 'mismo_dia';
  let quien = r.quien;
  if (!QUIEN_RECOGE.some(q => q.clave === quien)) quien = tipo === 'otro_dia' ? 'otro_evento' : 'responsable';
  return {
    tipo,
    fecha: tipo === 'otro_dia' && r.fecha ? String(r.fecha).slice(0, 10) : null,
    hora: r.hora || g.hora_recogida || null,
    quien,
    minutos: quien === 'otro_evento' ? Math.max(0, entero(r.minutos)) : 0,
  };
};

// Minutos y coste del responsable con la recogida (réplica de
// coste_responsable_con_recogida del backend): si recoge otra persona, los
// tramos de la recogida van a su tarifa; aprovechando otro evento, sin coste
// ni segundo viaje (salvo minutos imputados, a la tarifa propia).
// `sinCostePropio`: la persona elegida del equipo propio es un socio; en la
// opción "equipo propio" sus horas (y la recogida del equipo propio) cuestan 0.
export const costeResponsableConRecogida = (tramos = {}, tarifasBase = {}, modo = null, recogida = null,
  sinCostePropio = false) => {
  const t = {};
  CLAVES.forEach((k) => { t[k] = Math.max(0, entero(tramos?.[k])); });
  const modoCol = modo === 'extra' || modo === 'propio' ? modo : 'propio';
  const tarifas = sinCostePropio && modoCol === 'propio' ? { ...(tarifasBase || {}), propio: 0 } : tarifasBase;
  const tarifa = Number(tarifas?.[modoCol]) || 0;
  const total = CLAVES.reduce((s, k) => s + t[k], 0);
  const dos = viajesDeTramos(t) > 1;
  const quien = dos ? (recogida?.quien || null) : null;
  if (!dos || quien === null || quien === 'responsable' || quien === modoCol) {
    const imp = redondear2(total / 60 * tarifa);
    return {
      minutos: total, minutosResponsable: total, importe: imp, importeResponsable: imp,
      recogida: { quien: dos ? 'responsable' : null, minutos: 0, importe: 0, aparte: false },
      viajes: dos ? 2 : 1,
    };
  }
  const segundo = TRAMOS_RECOGIDA.reduce((s, k) => s + t[k], 0);
  const primero = total - segundo;
  const impResp = redondear2(primero / 60 * tarifa);
  let m;
  let impRec;
  let viajes;
  if (quien === 'otro_evento') {
    m = Math.max(0, entero(recogida?.minutos));
    impRec = redondear2(m / 60 * (Number(tarifas?.propio) || 0));
    viajes = 1;
  } else {
    m = segundo;
    impRec = redondear2(m / 60 * (Number(tarifas?.[quien]) || 0));
    viajes = 2;
  }
  return {
    minutos: primero + m, minutosResponsable: primero, importe: redondear2(impResp + impRec), importeResponsable: impResp,
    recogida: { quien, minutos: m, importe: impRec, aparte: true },
    viajes,
  };
};

export const calcularQuienVa = (tramos = {}, tarifas = {}, filasMenu = [], modo = null, recogida = null,
  sinCostePropio = false) => {
  const tarifaExtra = Number(tarifas.extra) || 0;
  const cPropio = costeResponsableConRecogida(tramos, tarifas, 'propio', recogida, sinCostePropio);
  const cExtra = costeResponsableConRecogida(tramos, tarifas, 'extra', recogida);
  const elegido = modo === 'extra' ? cExtra : cPropio;
  const totalMinutos = elegido.minutos;
  const horas = totalMinutos / 60;
  const costePropio = cPropio.importe;
  const costeExtra = cExtra.importe;
  const diferencia = redondear2(costeExtra - costePropio);
  let masBarato = 'igual';
  if (Math.abs(diferencia) >= 0.005) masBarato = diferencia > 0 ? 'propio' : 'extra';
  const menu = totalPersonalMenu(filasMenu, tarifaExtra);
  const totalConPropio = redondear2(costePropio + menu.coste);
  const totalConExtra = redondear2(costeExtra + menu.coste);
  let total = null;
  if (modo === 'extra') total = totalConExtra;
  else if (modo === 'propio') total = totalConPropio;
  return {
    totalMinutos, horas: redondear2(horas), costePropio, costeExtra, diferencia, masBarato,
    personalMenu: menu, totalConPropio, totalConExtra, total,
    personasExternas: menu.personas + (modo === 'extra' ? 1 : 0)
      + (modo !== 'extra' && elegido.recogida.quien === 'extra' ? 1 : 0),
    recogida: elegido.recogida,
    minutosResponsable: elegido.minutosResponsable,
    viajes: elegido.viajes,
    tarifaPropio: Number(tarifas.propio) || 0,
    tarifaExtra,
    sinCostePropio: !!sinCostePropio,
  };
};

// 'HH:MM' (o un texto que la contenga) -> minutos desde las 00:00
export const minutosDeHora = (valor) => {
  if (!valor) return null;
  const m = String(valor).match(/(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const mi = parseInt(m[2], 10);
  if (h > 23 || mi > 59) return null;
  return h * 60 + mi;
};

export const horaTexto = (minutos) => {
  const m = ((minutos % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};

// Minutos desde las 00:00 del día de referencia -> { hora: 'HH:MM', dia: +1/-1/0 }
const horaDia = (min) => ({ hora: horaTexto(min), dia: Math.floor(min / 1440) });

// Tramos seguidos desde `inicio` (réplica de _bloque_tiempo del backend)
const bloqueTiempo = (inicio, claves, t, fecha = null) => {
  let cursor = inicio;
  const tramos = claves.map((k) => {
    const m = Math.max(0, t[k] || 0);
    const a = horaDia(cursor);
    const b = horaDia(cursor + m);
    cursor += m;
    return { clave: k, inicio: a.hora, fin: b.hora, dia_inicio: a.dia, dia_fin: b.dia, minutos: m };
  });
  const a = horaDia(inicio);
  const b = horaDia(cursor);
  return {
    fecha: fecha ? String(fecha).slice(0, 10) : null,
    inicio: a.hora, fin: b.hora, dia_inicio: a.dia, dia_fin: b.dia, minutos: cursor - inicio, tramos,
  };
};

// Línea de tiempo del responsable con horas de reloj (réplica de
// horario_jornada del backend): hacia atrás desde la hora de inicio del evento
// (carga, ida, montaje) y hacia delante tras el evento (desmontaje, vuelta,
// descarga). Si se va y vuelve a recoger, el segundo bloque sale a la hora de
// recogida (la ida a recoger termina a esa hora); otro día, en su fecha.
export const horarioJornada = (horaInicio, tramos = {}, horaRecogida = null, otroDia = false,
  fechaEvento = null, fechaRecogida = null) => {
  const ini = minutosDeHora(horaInicio);
  if (ini === null) return null;
  const t = {};
  CLAVES.forEach((k) => { t[k] = entero(tramos?.[k]); });
  const llegada = ini - (t.carga + t.ida + t.montaje);
  const total = CLAVES.reduce((s, k) => s + t[k], 0);
  let recogida = minutosDeHora(horaRecogida);
  const fechaEv = fechaEvento ? String(fechaEvento).slice(0, 10) : null;
  if (viajesDeTramos(t) > 1 && recogida !== null) {
    const primero = bloqueTiempo(llegada, PRIMER_VIAJE, t, fechaEv);
    let segundo;
    if (otroDia) {
      segundo = bloqueTiempo(recogida - t.ida_recogida, TRAMOS_RECOGIDA, t, fechaRecogida);
    } else {
      if (recogida < ini) recogida += 1440;
      segundo = bloqueTiempo(recogida - t.ida_recogida, TRAMOS_RECOGIDA, t, fechaEv);
    }
    return {
      llegadaNave: horaTexto(llegada),
      finPrimerViaje: primero.fin,
      salidaRecogida: segundo.inicio,
      horaRecogida: horaTexto(recogida),
      finDescarga: segundo.fin,
      dosViajes: true,
      otroDia: !!otroDia,
      fechaRecogida: otroDia && fechaRecogida ? String(fechaRecogida).slice(0, 10) : null,
      totalMinutos: total,
      bloques: [primero, segundo],
    };
  }
  const orden = viajesDeTramos(t) === 1 ? SE_QUEDA : [...PRIMER_VIAJE, ...TRAMOS_RECOGIDA];
  return {
    llegadaNave: horaTexto(llegada),
    finDescarga: horaTexto(llegada + total),
    totalMinutos: total,
    bloques: [bloqueTiempo(llegada, orden, t, fechaEv)],
  };
};

// "05:30" / "00:15 (+1)"
export const horaConDia = (hora, dia) => (dia > 0 ? `${hora} (+${dia})` : (dia < 0 ? `${hora} (${dia})` : hora));

// Minutos entre dos horas "HH:MM" del mismo día de referencia (con días de diferencia)
export const minutosEntre = (desde, hasta) => {
  const a = minutosDeHora(desde);
  const b = minutosDeHora(hasta);
  if (a === null || b === null) return null;
  return b - a;
};

/**
 * Estado de Quién va de un evento o de una jornada, calculado al momento con
 * lo que se está editando (mismo cálculo que el backend y el motor de costes):
 * filas del personal del menú, tramos según se quede o se vaya y vuelva,
 * cálculo de costes, horario y comparativa se queda / se va y vuelve.
 * `grupo`: { tramos_base, personal_menu, espera_min, espera_max_min, km,
 * hora_inicio, hora_fin } tal como lo devuelve el backend.
 */
export const calcularEstadoGrupo = ({
  grupo, menuEditado = {}, valores = {}, editados = [], permanencia = 'se_queda',
  horaRecogida = null, tarifas = {}, costeKm = 0.1, modo = null, recogida = null, sinCostePropio = false,
}) => {
  const filas = resolverPersonalMenu(grupo?.personal_menu || [], menuEditado);
  const base = grupo?.tramos_base || grupo?.tramos_defecto || {};
  // El camarero que cubre quien va termina con el servicio: sus horas (p. ej.
  // las 4 h que se cobran) no alargan el evento (réplica de tramos_por_defecto)
  const servicioMenu = minutosServicioCubierto(filas);
  const cubiertos = entero(base.evento) > 0 ? Math.min(servicioMenu, entero(base.evento)) : servicioMenu;
  const dosViajes = permanencia === 'se_va';
  // Recogida (solo si se va y vuelve): tipo, fecha, hora, quién recoge y minutos
  const rec = dosViajes ? {
    tipo: recogida?.tipo === 'otro_dia' ? 'otro_dia' : 'mismo_dia',
    fecha: recogida?.fecha || null,
    hora: recogida?.hora || horaRecogida || null,
    quien: recogida?.quien || (recogida?.tipo === 'otro_dia' ? 'otro_evento' : 'responsable'),
    minutos: entero(recogida?.minutos),
  } : null;
  const otroDia = !!rec && rec.tipo === 'otro_dia';
  const dentro = entero(grupo?.minutos_dentro);
  const defecto = aplicarPermanencia(base, dosViajes, cubiertos, otroDia, dentro);
  const tramos = resolverTramos(defecto, valores, editados);
  const calculo = calcularQuienVa(tramos, tarifas, filas, modo, rec, sinCostePropio);
  const km = numero(grupo?.km);
  const comparativa = {};
  const recComparar = rec || { tipo: 'mismo_dia', quien: 'responsable', minutos: 0 };
  ['se_queda', 'se_va'].forEach((p) => {
    const seVaP = p === 'se_va';
    const tr = resolverTramos(aplicarPermanencia(base, seVaP, cubiertos, seVaP && recComparar.tipo === 'otro_dia', dentro),
      valores, editados);
    const r = seVaP ? recComparar : null;
    const cP = costeResponsableConRecogida(tr, tarifas, 'propio', r, sinCostePropio);
    const cE = costeResponsableConRecogida(tr, tarifas, 'extra', r);
    const elegido = modo === 'extra' ? cE : cP;
    const gasoilP = redondear2(km * 2 * cP.viajes * (Number(costeKm) || 0));
    const gasoilE = redondear2(km * 2 * cE.viajes * (Number(costeKm) || 0));
    comparativa[p] = {
      minutos: elegido.minutos,
      horas: redondear2(elegido.minutos / 60),
      viajes: elegido.viajes,
      gasoil: modo === 'extra' ? gasoilE : gasoilP,
      propio: redondear2(cP.importe + gasoilP),
      extra: redondear2(cE.importe + gasoilE),
    };
  });
  const espera = grupo?.espera_min;
  const esperaMax = entero(grupo?.espera_max_min) || 120;
  const sugerirRecogida = !dosViajes && espera !== null && espera !== undefined
    && entero(espera) > esperaMax && cubiertos <= 0;
  const avisos = [];
  // Hora de fin sin definir (o igual a la de inicio): el backend calcula con la
  // duración típica del servicio y pide poner la hora real
  if (grupo?.fin_sin_definir && grupo?.aviso_fin_sin_definir && !(editados || []).includes('evento')) {
    avisos.push(grupo.aviso_fin_sin_definir);
  }
  // Dudas para Alfonso: las horas del camarero pasan de la duración del servicio
  if (servicioMenu > cubiertos && cubiertos > 0 && !dosViajes && !(editados || []).includes('evento')) {
    avisos.push(`Revisar horario: el servicio de camarero tiene ${formatoMinutos(servicioMenu)} y el servicio dura ${formatoMinutos(cubiertos)}. El tramo Evento se queda en ${formatoMinutos(cubiertos)} (el camarero termina con el servicio); si tiene que quedarse más, amplía el tramo Evento.`);
  }
  if (cubiertos > 0 && entero(tramos.evento) < cubiertos) {
    if (viajesDeTramos(tramos) > 1 && !otroDia) {
      avisos.push(`La persona que va al evento cubre un servicio de ${formatoMinutos(cubiertos)}, pero se va y vuelve a recoger: desmarca "Recogida más tarde" o la casilla del servicio.`);
    } else if ((editados || []).includes('evento')) {
      avisos.push(`La persona que va al evento cubre un servicio de ${formatoMinutos(cubiertos)} y el tramo Evento solo tiene ${formatoMinutos(tramos.evento)}: amplíalo o desmarca la casilla del servicio.`);
    }
  }
  // Los solapes de montaje no van en avisos: van en `solapes` con su decisión
  // (la tarjeta pinta "Añadir 1 extra" / "Ignorar" o "restablecer")
  const horaRec = dosViajes ? ((rec && rec.hora) || grupo?.hora_fin) : grupo?.hora_fin;
  return {
    filas,
    defecto,
    tramos,
    calculo,
    comparativa,
    cubiertos,
    recogida: rec,
    eventoAmpliado: !dosViajes && cubiertos > entero(base.evento),
    viajes: viajesDeTramos(tramos),
    sugerenciaRecogida: sugerirRecogida ? `La espera sería de ${formatoMinutos(espera)}: puede compensar recoger más tarde.` : null,
    avisos,
    solapes: Array.isArray(grupo?.solapes) ? grupo.solapes : [],
    horaRecogida: horaRec,
    horario: horarioJornada(grupo?.hora_inicio, tramos, horaRec, otroDia, grupo?.fecha || null, rec?.fecha || null),
    lineaDia: grupo?.linea_dia || null,
  };
};

const ETIQUETAS_TRAMO_LINEA = {
  carga: 'Carga', ida: 'Ida', montaje: 'Montaje', vuelta_montaje: 'Vuelta a la nave',
  ida_recogida: 'Ida a recoger', desmontaje: 'Desmontaje', vuelta: 'Vuelta', descarga: 'Descarga',
};

/**
 * Línea del día con horas de reloj: los tramos del responsable (salvo
 * "evento") intercalados con los servicios, los montajes intermedios (desde
 * dentro) y las esperas. `horario`: el de horarioJornada; `lineaDia`: la del
 * backend (items en minutos desde las 00:00 del día del evento).
 * Devuelve [{ clave, texto, tipo, solape }] en orden de hora.
 */
export const lineaDelDia = (horario, lineaDia) => {
  if (!horario || !lineaDia || !Array.isArray(lineaDia.items)) return [];
  const bloques = horario.bloques || [];
  if (bloques.length === 0) return [];
  const absoluto = (hora, dia) => (minutosDeHora(hora) ?? 0) + (entero(dia) * 1440);
  const primerServicio = lineaDia.items.find(x => x.tipo === 'servicio');
  const segmentos = [];
  bloques.forEach((b, j) => {
    b.tramos.forEach((t, i) => {
      if (t.clave === 'evento' || entero(t.minutos) <= 0) return;
      let etiqueta = ETIQUETAS_TRAMO_LINEA[t.clave] || t.clave;
      if (t.clave === 'montaje' && primerServicio) etiqueta = `Montaje ${primerServicio.nombre}`;
      const fecha = horario.otroDia && b.fecha ? ` (${String(b.fecha).slice(8, 10)}/${String(b.fecha).slice(5, 7)})` : '';
      segmentos.push({
        clave: `tramo-${j}-${t.clave}`,
        tipo: 'tramo',
        orden: j * 100 + i,
        // Otro día: el segundo bloque va siempre al final
        inicio: absoluto(t.inicio, t.dia_inicio) + (horario.otroDia && j > 0 ? 100000 : 0),
        texto: `${etiqueta} ${horaConDia(t.inicio, t.dia_inicio)} - ${horaConDia(t.fin, t.dia_fin)}${fecha}`,
      });
    });
  });
  lineaDia.items.forEach((it, i) => {
    const tramo = `${horaTexto(it.inicio)} - ${horaTexto(it.fin)}`;
    let texto;
    if (it.tipo === 'servicio') {
      texto = `${it.nombre} ${tramo}`;
    } else if (it.tipo === 'montaje') {
      texto = `Montaje ${it.nombre} ${tramo} (desde dentro${entero(it.solape_min) > 0 ? `; solapa ${formatoMinutos(it.solape_min)} con el servicio anterior` : ''})`;
    } else {
      texto = `${it.larga ? 'Espera larga' : 'Espera'} ${tramo} (${formatoMinutos(it.minutos)})`;
    }
    segmentos.push({
      clave: `item-${i}`, tipo: it.tipo, orden: 50 + i, inicio: it.inicio, texto,
      solape: entero(it.solape_min) > 0,
    });
  });
  // Por hora; a la misma hora, en el orden de la línea
  return segmentos.sort((a, b) => (a.inicio - b.inicio) || (a.orden - b.orden));
};

// "2 h 30 min", "45 min", "3 h"
export const formatoMinutos = (min) => {
  const m = Math.max(0, entero(min));
  const h = Math.floor(m / 60);
  const resto = m % 60;
  if (h === 0) return `${resto} min`;
  return resto === 0 ? `${h} h` : `${h} h ${resto} min`;
};

const textoHoras = (h) => String(redondear2(numero(h))).replace('.', ',');

// "Cóctel · 180 pax · 9 camareros + 3 extras · 4 h"
// "180 pax" o, en las líneas de servicio de camarero por horas, "3 unidades"
export const textoCantidadFila = (f) => {
  if (f?.por_horas) {
    const u = redondear2(numero(f.por_horas.unidades));
    return `${String(u).replace('.', ',')} ${u === 1 ? 'unidad' : 'unidades'}`;
  }
  return `${f?.personas ?? 0} pax`;
};

export const textoFilaPersonalMenu = (f) => {
  const cam = entero(f.camareros);
  const ext = entero(f.extras);
  if (cam + ext === 0) return `${f.nombre} · ${textoCantidadFila(f)} · sin personal del menú`;
  const tandas = Array.isArray(f.tandas) ? f.tandas : [];
  if (tandas.length > 0) {
    // "Cóctel · 180 pax · 2 camareros de 11:00 a 15:00 y 3 de 13:00 a 17:00 + 1 extra · 4,5 h"
    const textoTandas = tandas.map((t, i) => {
      const n = entero(t.camareros);
      const quien = i === 0 ? `${n} ${n === 1 ? 'camarero' : 'camareros'}` : `${n}`;
      return `${quien} de ${t.hora_inicio} a ${t.hora_fin}`;
    }).join(' y ');
    const extras = ext > 0 ? ` + ${ext} ${ext === 1 ? 'extra' : 'extras'} · ${textoHoras(f.horas)} h` : '';
    return `${f.nombre} · ${textoCantidadFila(f)} · ${textoTandas}${extras}`;
  }
  const partes = [];
  if (cam > 0) partes.push(`${cam} ${cam === 1 ? 'camarero' : 'camareros'}`);
  if (ext > 0) partes.push(`${ext} ${ext === 1 ? 'extra' : 'extras'}`);
  return `${f.nombre} · ${textoCantidadFila(f)} · ${partes.join(' + ')} · ${textoHoras(f.horas)} h`;
};

// Etiqueta (como `servicio`) de los grupos del responsable y de la recogida
const ETIQUETA_ORIGEN = { responsable: 'Responsable del montaje', recogida: 'Recogida' };

// "HH:MM" normalizada o '' si no es una hora válida
const horaNormalizada = (valor) => {
  const m = minutosDeHora(valor);
  return m === null ? '' : horaTexto(m);
};

/**
 * Servicio de una fila del personal del menú y su hora, para enseñarlo junto
 * a su grupo en la solicitud de personal extra (solo informativo): el nombre
 * de la línea ("Cóctel frío") y la hora de servicio de la línea
 * (`hora_inicio_servicio` / `hora_fin_servicio`, las de Líneas y menús).
 * `sin_hora_servicio` solo si se sabe que la línea no tiene hora (la fila trae
 * el campo vacío); una fila sin el campo (lo guardado antes de cargar el
 * estado) no lleva la marca. Las filas de solape, con su tramo.
 */
export const servicioDeFila = (f) => {
  const servicio = String(f?.nombre || '').trim();
  if (esSolape(f)) {
    return {
      servicio, servicio_hora_inicio: horaNormalizada(f?.hora_inicio), servicio_hora_fin: horaNormalizada(f?.hora_fin),
    };
  }
  const ini = horaNormalizada(f?.hora_inicio_servicio);
  const fin = horaNormalizada(f?.hora_fin_servicio);
  const res = { servicio, servicio_hora_inicio: ini, servicio_hora_fin: fin };
  if (!ini && f && Object.prototype.hasOwnProperty.call(f, 'hora_inicio_servicio')) res.sin_hora_servicio = true;
  return res;
};

/**
 * Borrador de la orden de personal extra: un grupo para el responsable del
 * montaje si es un extra (de la llegada a la nave al fin de la descarga) y un
 * grupo por menú con camareros o extras (desde la llegada al lugar, con las
 * horas del menú). Devuelve null si no hay nadie externo.
 * Si se va y vuelve a recoger, la misma persona va en UN grupo con dos tramos
 * (`tramos: [{inicio, fin, fecha?}]`; otro día, con la fecha de cada uno): es
 * 1 persona. Si recoge otro extra distinto (el responsable es propio), va en un
 * grupo aparte con el tramo de la recogida (`origen: 'recogida'`).
 */
export const sugerenciaOrdenExtra = ({
  modo, tramos, horaInicio, horaLlegada, horaRecogida = null, filasMenu = [], tarifaExtra,
  recogida = null, fechaEvento = null,
}) => {
  const grupos = [];
  const detalle = [];
  const otroDia = recogida?.tipo === 'otro_dia';
  const horario = horarioJornada(horaInicio, tramos, recogida?.hora || horaRecogida, otroDia,
    fechaEvento, recogida?.fecha || null);
  const dos = !!(horario && horario.dosViajes);
  const quien = dos ? (recogida?.quien || 'responsable') : null;
  const fechaCorta = (f) => (f ? `${String(f).slice(8, 10)}/${String(f).slice(5, 7)}` : '');
  const tramoPrimero = dos ? {
    inicio: horario.llegadaNave, fin: horario.finPrimerViaje, ...(otroDia && fechaEvento ? { fecha: String(fechaEvento).slice(0, 10) } : {}),
  } : null;
  const tramoRecogida = dos ? {
    inicio: horario.salidaRecogida, fin: horario.finDescarga, ...(otroDia && recogida?.fecha ? { fecha: recogida.fecha } : {}),
  } : null;
  const textoTramo = (t) => `${t.fecha ? `el ${fechaCorta(t.fecha)} ` : ''}de ${t.inicio} a ${t.fin}`;
  if (modo === 'extra') {
    const total = CLAVES.reduce((s, t) => s + entero(tramos?.[t]), 0);
    const tramosTexto = TRAMOS_QUIEN_VA
      .filter(t => !t.segundoViaje || entero(tramos?.[t.clave]) > 0)
      .map(t => `${t.etiqueta.toLowerCase()} ${formatoMinutos(tramos?.[t.clave])}`)
      .join(', ');
    if (dos && (quien === 'responsable' || quien === 'extra')) {
      // La misma persona en dos tramos (la espera no se cuenta): 1 persona
      grupos.push({
        origen: 'responsable', num_personas: 1, hora_inicio: tramoPrimero.inicio, hora_fin: tramoPrimero.fin,
        horas: null, tramos: [tramoPrimero, tramoRecogida], servicio: ETIQUETA_ORIGEN.responsable,
      });
      detalle.push(`Responsable del montaje (1 persona, dos tramos): ${textoTramo(tramoPrimero)} (carga, montaje y vuelta) y ${textoTramo(tramoRecogida)} (recogida a las ${horario.horaRecogida}): ${tramosTexto}.`);
    } else if (dos) {
      // Monta el extra; recoge el equipo propio o se aprovecha otro evento
      grupos.push({
        origen: 'responsable', num_personas: 1, hora_inicio: tramoPrimero.inicio, hora_fin: tramoPrimero.fin, horas: null,
        servicio: ETIQUETA_ORIGEN.responsable,
      });
      detalle.push(`Responsable del montaje (1 persona): ${textoTramo(tramoPrimero)} (carga, montaje y vuelta). La recogida no la hace el extra.`);
    } else {
      grupos.push({
        // `origen`: qué cubre el grupo en el coste (responsable o personal del menú)
        origen: 'responsable',
        num_personas: 1,
        hora_inicio: horario ? horario.llegadaNave : '',
        hora_fin: horario ? horario.finDescarga : '',
        horas: horario ? null : redondear2(total / 60),
        servicio: ETIQUETA_ORIGEN.responsable,
      });
      detalle.push(`Responsable del montaje (1 persona)${horario ? `, de ${horario.llegadaNave} a ${horario.finDescarga}` : ''}: ${tramosTexto}.`);
    }
  } else if (dos && quien === 'extra') {
    // El responsable es del equipo propio y recoge un extra: 1 persona, solo la recogida
    grupos.push({
      origen: 'recogida', num_personas: 1, hora_inicio: tramoRecogida.inicio, hora_fin: tramoRecogida.fin, horas: null,
      servicio: ETIQUETA_ORIGEN.recogida,
      ...(tramoRecogida.fecha ? { tramos: [tramoRecogida] } : {}),
    });
    detalle.push(`Recogida (1 persona): ${textoTramo(tramoRecogida)} (recogida a las ${horario.horaRecogida}).`);
  }
  const llegada = minutosDeHora(horaLlegada) !== null ? horaLlegada : horaInicio;
  const inicioLugar = minutosDeHora(llegada);
  (filasMenu || []).forEach((f) => {
    // Las filas que cubre la persona que va al evento no van a la solicitud
    const n = personasFila(f);
    if (n <= 0) return;
    const horas = numero(f.horas);
    // Solape de montaje (hora por servicio): en su tramo de reloj
    const inicioFila = esSolape(f) && minutosDeHora(f.hora_inicio) !== null ? minutosDeHora(f.hora_inicio) : inicioLugar;
    const grupoDesdeLlegada = (personas) => ({
      origen: 'personal_menu',
      num_personas: personas,
      hora_inicio: inicioFila !== null ? horaTexto(inicioFila) : '',
      hora_fin: inicioFila !== null ? horaTexto(inicioFila + Math.round(horas * 60)) : '',
      horas: inicioFila !== null ? null : redondear2(horas),
      // Servicio y su hora: solo informativo (no cambia Entrada ni Salida)
      ...servicioDeFila(f),
    });
    const tandas = Array.isArray(f.tandas) ? f.tandas.filter(t => entero(t.camareros) > 0) : [];
    if (tandas.length > 0) {
      // Tandas de camareros: un grupo por tanda con su entrada y su salida; los
      // extras de la fila, en otro grupo como siempre
      tandas.forEach((t) => {
        grupos.push({
          origen: 'personal_menu',
          num_personas: entero(t.camareros),
          hora_inicio: normalizarHoraEscrita(t.hora_inicio),
          hora_fin: normalizarHoraEscrita(t.hora_fin),
          horas: null,
          ...servicioDeFila(f),
        });
      });
      if (entero(f.extras) > 0) grupos.push(grupoDesdeLlegada(entero(f.extras)));
    } else {
      grupos.push(grupoDesdeLlegada(n));
    }
    detalle.push(`${textoFilaPersonalMenu(f)}.`);
  });
  if (grupos.length === 0) return null;
  return {
    grupos,
    total_personas: grupos.reduce((s, g) => s + g.num_personas, 0),
    tarifa_hora: tarifaExtra,
    observaciones: detalle.join(' '),
  };
};

/**
 * Texto pequeño de la fila de un grupo: el servicio y su hora ("Cóctel frío ·
 * empieza a las 13:00 (hasta las 15:00)"), "· sin hora de servicio" si la
 * línea no la tiene, o la etiqueta del responsable o la recogida (que van
 * como `servicio`). Órdenes guardadas sin estos campos: null (se enseñan como
 * hasta ahora).
 */
export const textoServicioGrupo = (grupo) => {
  const servicio = String(grupo?.servicio || '').trim();
  if (servicio) {
    if (grupo.servicio_hora_inicio) {
      return `${servicio} · empieza a las ${grupo.servicio_hora_inicio}${grupo.servicio_hora_fin ? ` (hasta las ${grupo.servicio_hora_fin})` : ''}`;
    }
    return grupo.sin_hora_servicio ? `${servicio} · sin hora de servicio` : servicio;
  }
  return null;
};

// ===== Estado editable de la tarjeta Quién va (compartido por Logística y Logística 2) =====

const r2 = (n) => Math.round(((Number(n) || 0) + Number.EPSILON) * 100) / 100;

// Solo lo editado a mano cuenta como cambio
export const firmaEstado = (modo, editados, tramos, menuEditado, permanencia, recogida) => JSON.stringify([
  modo || null,
  [...(editados || [])].sort(),
  [...(editados || [])].sort().map(t => Number(tramos?.[t]) || 0),
  Object.keys(menuEditado || {}).sort().map(k => [k, menuEditado[k]]),
  permanencia === 'se_va' ? 'se_va' : 'se_queda',
  permanencia === 'se_va'
    ? [recogida?.tipo || 'mismo_dia', recogida?.fecha || '', recogida?.hora || '', recogida?.quien || '',
      recogida?.quien === 'otro_evento' ? (Number(recogida?.minutos) || 0) : 0]
    : null,
]);

// Quién recoge por defecto: el mismo día, el mismo responsable; otro día, aprovechando otro evento
export const quienPorDefecto = (tipo) => (tipo === 'otro_dia' ? 'otro_evento' : 'responsable');

// Quita del objeto de edición del menú los campos vacíos y las claves sin cambios
export const limpiarMenuEditado = (editado) => {
  const res = {};
  Object.entries(editado || {}).forEach(([clave, campos]) => {
    const limpio = {};
    Object.entries(campos || {}).forEach(([c, v]) => {
      if (c === 'tandas') {
        // Tandas de camareros: lista limpia (sin tandas, nada)
        const tandas = limpiarTandas(v);
        if (tandas.length > 0) limpio.tandas = tandas;
      } else if (typeof v === 'boolean') limpio[c] = v;
      else if (v !== undefined && v !== null && v !== '') limpio[c] = Number(v);
    });
    // Con tandas, camareros y horas salen de ellas (como en el servidor)
    if (limpio.tandas) {
      limpio.camareros = limpio.tandas.reduce((s, t) => s + t.camareros, 0);
      limpio.horas = horasDeTandas(limpio.tandas);
    }
    if (Object.keys(limpio).length > 0) res[clave] = Object.fromEntries(Object.keys(limpio).sort().map(c => [c, limpio[c]]));
  });
  return res;
};

// Quién va por jornada (multievento), limpio y ordenado para comparar y
// guardar: {jornada_id: {modo?, personal_menu_editado?}}. Solo lo propio de cada
// jornada; lo que no está, la jornada lo hereda del evento.
export const limpiarPorJornada = (porJornada) => {
  const res = {};
  Object.keys(porJornada || {}).sort().forEach((jid) => {
    const pj = porJornada[jid] || {};
    const limpio = {};
    if (pj.modo === 'propio' || pj.modo === 'extra') limpio.modo = pj.modo;
    const pme = limpiarMenuEditado(pj.personal_menu_editado);
    if (Object.keys(pme).length > 0) {
      limpio.personal_menu_editado = Object.fromEntries(Object.keys(pme).sort().map(k => [k, pme[k]]));
    }
    if (Object.keys(limpio).length > 0) res[jid] = limpio;
  });
  return res;
};

// Suma de los cálculos de varias jornadas (multievento). `modos`: el modo de
// cada jornada (su quién va); sin él, el del evento para todas.
export const sumarCalculos = (calculos, modo, modos = null) => {
  const s = (k) => r2(calculos.reduce((t, c) => t + (Number(c[k]) || 0), 0));
  const costePropio = s('costePropio');
  const costeExtra = s('costeExtra');
  const diferencia = r2(costeExtra - costePropio);
  const menu = {
    personas: calculos.reduce((t, c) => t + c.personalMenu.personas, 0),
    horasPersona: r2(calculos.reduce((t, c) => t + c.personalMenu.horasPersona, 0)),
    coste: r2(calculos.reduce((t, c) => t + c.personalMenu.coste, 0)),
  };
  const totalConPropio = s('totalConPropio');
  const totalConExtra = s('totalConExtra');
  let total = null;
  if (Array.isArray(modos) && modos.length === calculos.length && modos.some(m => m && m !== modo)) {
    // Cada jornada con su modo: sin modo en alguna, sin total (igual que sin elegir)
    total = modos.every(m => m === 'propio' || m === 'extra')
      ? r2(calculos.reduce((t, c, i) => t + (Number(modos[i] === 'extra' ? c.totalConExtra : c.totalConPropio) || 0), 0))
      : null;
  } else if (modo === 'extra') total = totalConExtra;
  else if (modo === 'propio') total = totalConPropio;
  const totalMinutos = calculos.reduce((t, c) => t + c.totalMinutos, 0);
  let masBarato = 'igual';
  if (Math.abs(diferencia) >= 0.005) masBarato = diferencia > 0 ? 'propio' : 'extra';
  return {
    totalMinutos, horas: r2(totalMinutos / 60), costePropio, costeExtra, diferencia, masBarato,
    personalMenu: menu, totalConPropio, totalConExtra, total,
  };
};

export const sumarComparativas = (comparativas) => {
  const res = {};
  ['se_queda', 'se_va'].forEach((p) => {
    const filas = comparativas.map(c => c[p]);
    res[p] = {
      minutos: filas.reduce((t, f) => t + f.minutos, 0),
      horas: r2(filas.reduce((t, f) => t + f.horas, 0)),
      gasoil: r2(filas.reduce((t, f) => t + f.gasoil, 0)),
      propio: r2(filas.reduce((t, f) => t + f.propio, 0)),
      extra: r2(filas.reduce((t, f) => t + f.extra, 0)),
    };
  });
  return res;
};

// Tarifa por minuto del personal que va en la furgoneta según quién va al
// evento (réplica de tarifa_minuto_personal del backend): extra o equipo
// propio; sin elegir, equipo propio por defecto. Tarifas de las reglas
// comerciales (quien_va_evento.coste_hora_propio / coste_hora_extra).
export const tarifaTransportePersonal = (modo, reglas) => {
  const cfg = reglas?.quien_va_evento || {};
  const porDefecto = modo !== 'propio' && modo !== 'extra';
  const modoUsado = porDefecto ? 'propio' : modo;
  let hora = numero(modoUsado === 'extra' ? cfg.coste_hora_extra : cfg.coste_hora_propio);
  if (hora <= 0) hora = numero(reglas?.transporte?.coste_minuto_personal) * 60;
  const etiqueta = porDefecto ? 'equipo propio por defecto' : (modoUsado === 'extra' ? 'extra' : 'equipo propio');
  return { modo: modoUsado, porDefecto, tarifaHora: hora, tarifaMinuto: hora / 60, etiqueta };
};
